import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, ChevronRight, ChevronLeft, Upload, FileText, ShieldCheck, Briefcase, Award, User, Building2, Loader2 } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const STEPS = [
  { key: "personal", label: "Personal Details", icon: User },
  { key: "address", label: "Address & Emergency", icon: Building2 },
  { key: "documents", label: "Document Upload", icon: FileText },
  { key: "bank", label: "Bank & Tax", icon: Briefcase },
  { key: "policies", label: "Policy Acknowledgement", icon: ShieldCheck },
  { key: "signature", label: "Sign & Submit", icon: Award },
];

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [saving, setSaving] = useState(false);
  const [resumeFile, setResumeFile] = useState(null);
  const [signature, setSignature] = useState(null);

  useEffect(() => {
    api.get("/onboarding").then((r) => {
      setList(r.data);
      if (r.data.length && !selectedId) {
        setSelectedId(r.data[0].id);
        setData(r.data[0].steps?.flat || {});
      }
    });
  }, []);

  const selected = list.find((x) => x.id === selectedId);
  const stepKey = STEPS[stepIdx].key;
  const progress = Math.round(((stepIdx + 1) / STEPS.length) * 100);

  const saveStep = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const payload = { step: stepKey, data, progress, status: stepIdx === STEPS.length - 1 ? "completed" : "in_progress" };
      await api.post(`/onboarding/${selectedId}/step`, payload);
      toast.success(`${STEPS[stepIdx].label} saved`);
      if (stepIdx < STEPS.length - 1) setStepIdx(stepIdx + 1);
      else {
        toast.success("Onboarding complete!");
        navigate("/onboarding");
      }
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const uploadDocument = async (e, category) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: rec } = await api.post(`/files/upload?category=${category}`, fd);
      setData((d) => ({ ...d, [category]: { file_id: rec.id, name: rec.original_filename } }));
      toast.success(`${category} uploaded`);
    } catch {
      toast.error("Upload failed");
    }
  };

  const submitSignature = async () => {
    if (!signature) { toast.error("Please sign first"); return; }
    try {
      await api.post("/signatures", {
        data_url: signature,
        related_to: `onboarding:${selectedId}`,
        purpose: "onboarding_acceptance",
      });
      setData((d) => ({ ...d, signed: true }));
      toast.success("Signature recorded");
    } catch {
      toast.error("Failed to record signature");
    }
  };

  return (
    <div data-testid="onboarding-wizard-page">
      <PageHeader
        title="Onboarding wizard"
        subtitle="Multi-step joiner onboarding — personal info, documents, policy acceptance, e-signature."
      />

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left: joiners list + steps */}
        <Card className="hr-card p-5 lg:col-span-1">
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">Joiners</div>
          <div className="space-y-1.5 mb-4">
            {list.map((o) => (
              <button
                key={o.id}
                data-testid={`onb-select-${o.id}`}
                onClick={() => { setSelectedId(o.id); setStepIdx(0); setData(o.steps?.flat || {}); }}
                className={`w-full text-left px-3 py-2 rounded-md text-sm border ${selectedId === o.id ? "bg-[#F26B3A] text-white border-[#F26B3A]" : "bg-white border-gray-200 hover:border-gray-300"}`}
              >
                <div className="font-medium truncate">{o.candidate_name}</div>
                <div className={`text-[11px] ${selectedId === o.id ? "text-white/70" : "text-gray-500"} truncate`}>{o.job_title}</div>
              </button>
            ))}
          </div>
          <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 mt-6">Steps</div>
          <ol className="space-y-1">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                data-testid={`wizard-step-${s.key}`}
                onClick={() => selectedId && setStepIdx(i)}
                className={`flex items-center gap-2 text-sm px-2.5 py-2 rounded-md cursor-pointer ${i === stepIdx ? "bg-[#1E88E5]/10 text-[#1E88E5]" : "text-gray-700 hover:bg-gray-50"}`}
              >
                <div className={`h-6 w-6 rounded-full grid place-items-center text-xs ${i < stepIdx ? "bg-emerald-500 text-white" : i === stepIdx ? "bg-[#1E88E5] text-white" : "bg-gray-200 text-gray-500"}`}>
                  {i < stepIdx ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className="text-xs">{s.label}</span>
              </li>
            ))}
          </ol>
        </Card>

        {/* Right: step content */}
        <Card className="hr-card p-6 lg:col-span-3">
          {!selected ? (
            <div className="text-sm text-gray-500">Select a joiner to begin.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="font-display text-xl font-semibold text-[#0F172A]">{STEPS[stepIdx].label}</div>
                  <div className="text-xs text-gray-500">Step {stepIdx + 1} of {STEPS.length} · {selected.candidate_name} · {selected.job_title}</div>
                </div>
                <div className="text-xs font-mono text-gray-500">{progress}%</div>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-6">
                <div className="h-full bg-[#1E88E5] transition-all" style={{ width: `${progress}%` }} />
              </div>

              {stepKey === "personal" && (
                <div className="grid grid-cols-2 gap-4" data-testid="wizard-content-personal">
                  <Field label="Full name"><Input value={data.full_name || ""} onChange={(e) => setData({ ...data, full_name: e.target.value })} /></Field>
                  <Field label="Date of birth"><Input type="date" value={data.dob || ""} onChange={(e) => setData({ ...data, dob: e.target.value })} /></Field>
                  <Field label="Gender"><Input value={data.gender || ""} onChange={(e) => setData({ ...data, gender: e.target.value })} /></Field>
                  <Field label="Blood group"><Input value={data.blood_group || ""} onChange={(e) => setData({ ...data, blood_group: e.target.value })} /></Field>
                  <Field label="Marital status"><Input value={data.marital_status || ""} onChange={(e) => setData({ ...data, marital_status: e.target.value })} /></Field>
                  <Field label="Nationality"><Input value={data.nationality || ""} onChange={(e) => setData({ ...data, nationality: e.target.value })} /></Field>
                </div>
              )}

              {stepKey === "address" && (
                <div className="grid grid-cols-2 gap-4" data-testid="wizard-content-address">
                  <div className="col-span-2"><Field label="Permanent address"><Textarea rows={2} value={data.address || ""} onChange={(e) => setData({ ...data, address: e.target.value })} /></Field></div>
                  <Field label="City"><Input value={data.city || ""} onChange={(e) => setData({ ...data, city: e.target.value })} /></Field>
                  <Field label="State"><Input value={data.state || ""} onChange={(e) => setData({ ...data, state: e.target.value })} /></Field>
                  <Field label="PIN"><Input value={data.pin || ""} onChange={(e) => setData({ ...data, pin: e.target.value })} /></Field>
                  <Field label="Emergency contact name"><Input value={data.emergency_name || ""} onChange={(e) => setData({ ...data, emergency_name: e.target.value })} /></Field>
                  <Field label="Emergency contact phone"><Input value={data.emergency_phone || ""} onChange={(e) => setData({ ...data, emergency_phone: e.target.value })} /></Field>
                  <Field label="Relation"><Input value={data.emergency_relation || ""} onChange={(e) => setData({ ...data, emergency_relation: e.target.value })} /></Field>
                </div>
              )}

              {stepKey === "documents" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="wizard-content-documents">
                  {["aadhaar", "pan", "passport", "resume", "education", "experience", "bank_proof", "photo"].map((cat) => (
                    <div key={cat} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium capitalize">{cat.replace("_", " ")}</div>
                        <div className="text-[11px] text-gray-500">{data[cat]?.name || "Not uploaded"}</div>
                      </div>
                      <label className="cursor-pointer text-xs px-2.5 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 inline-flex items-center gap-1.5">
                        <Upload className="h-3.5 w-3.5" /> Upload
                        <input type="file" className="hidden" onChange={(e) => uploadDocument(e, cat)} data-testid={`upload-${cat}`} />
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {stepKey === "bank" && (
                <div className="grid grid-cols-2 gap-4" data-testid="wizard-content-bank">
                  <Field label="Bank name"><Input value={data.bank_name || ""} onChange={(e) => setData({ ...data, bank_name: e.target.value })} /></Field>
                  <Field label="Account number"><Input value={data.account_no || ""} onChange={(e) => setData({ ...data, account_no: e.target.value })} /></Field>
                  <Field label="IFSC"><Input value={data.ifsc || ""} onChange={(e) => setData({ ...data, ifsc: e.target.value })} /></Field>
                  <Field label="PAN"><Input value={data.pan_no || ""} onChange={(e) => setData({ ...data, pan_no: e.target.value })} /></Field>
                  <Field label="Aadhaar"><Input value={data.aadhaar_no || ""} onChange={(e) => setData({ ...data, aadhaar_no: e.target.value })} /></Field>
                  <Field label="UAN (optional)"><Input value={data.uan || ""} onChange={(e) => setData({ ...data, uan: e.target.value })} /></Field>
                </div>
              )}

              {stepKey === "policies" && (
                <div className="space-y-3" data-testid="wizard-content-policies">
                  {[
                    "Code of Conduct",
                    "Information Security Policy",
                    "HIPAA Compliance Acknowledgement",
                    "Anti-Harassment Policy",
                    "Leave & Attendance Policy",
                    "Data Privacy (GDPR/DPDP)",
                  ].map((p) => (
                    <label key={p} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`policy-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        checked={!!data[`policy_${p}`]}
                        onChange={(e) => setData({ ...data, [`policy_${p}`]: e.target.checked })}
                        className="h-4 w-4 accent-[#F26B3A]"
                      />
                      <span className="text-sm">{p}</span>
                    </label>
                  ))}
                </div>
              )}

              {stepKey === "signature" && (
                <div data-testid="wizard-content-signature">
                  <p className="text-sm text-gray-600 mb-4">
                    By signing below, you confirm that the information provided is accurate and you agree to the policies acknowledged in the previous step.
                  </p>
                  <SignaturePad onChange={setSignature} />
                  <div className="mt-4">
                    <Button data-testid="record-signature" variant="outline" disabled={!signature} onClick={submitSignature}>
                      <Award className="h-4 w-4 mr-1.5" /> Record signature
                    </Button>
                    {data.signed && <span className="ml-2 text-xs text-emerald-700">✓ Signature on file</span>}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-100">
                <Button variant="outline" disabled={stepIdx === 0} onClick={() => setStepIdx(stepIdx - 1)} data-testid="wizard-prev">
                  <ChevronLeft className="h-4 w-4 mr-1" /> Back
                </Button>
                <Button onClick={saveStep} disabled={saving} className="bg-[#F26B3A] hover:bg-[#d85d2f]" data-testid="wizard-next">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <>
                    {stepIdx === STEPS.length - 1 ? "Finish onboarding" : "Save & continue"}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>}
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-gray-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
